import { createContext, useContext } from 'react'

/**
 * FAQ data fetching interface
 * 各アプリケーション（FAQ Client/Preview）で実装を提供する
 */
export interface FaqDataClient {
  /**
   * カテゴリに紐づくFAQ一覧を取得
   * @param params - botId, categories, tenantId
   * @returns FAQ一覧
   */
  fetchCategoryFaq(params: {
    botId: string
    categories: string[]
    tenantId?: string | null
  }): Promise<{
    filterByCategory: Array<{
      id: string
      title: string
    }>
  }>

  /**
   * よくあるご質問（閲覧数上位）を取得
   * @param params - botId, tenantId
   * @returns FAQ一覧
   */
  fetchTopPageViewFAQs(params: { botId: string; tenantId?: string | null }): Promise<{
    topPageViewFAQs: Array<{
      id: string
      title: string
    }>
  }>

  /**
   * FAQ検索を実行
   * @param params - botId, inputValue, searchType, tenantId
   * @returns 検索結果のFAQ一覧
   */
  searchFaq(params: {
    botId: string
    tenantId?: string | null
    inputValue: string
    searchType?: string | null
    count?: number
  }): Promise<{
    queryFAQ: Array<{
      id: string
      title: string
    }>
  }>

  /**
   * 検索履歴を記録
   * @param params - botId, text, tenantId
   * @returns 記録結果
   */
  recordSearched(params: {
    botId: string
    text: string
    tenantId?: string | null
  }): Promise<{ searched: { result: boolean } }>

  /**
   * 検索イベントを送信（アナリティクス用）
   * @param params - inputValue, result
   */
  sendSearchEvent(params: { inputValue: string; result: any[] }): void

  /**
   * フィードバックイベントを送信（アナリティクス用）
   * @param feedback - ok or ng
   */
  sendFeedbackEvent(feedback: 'ok' | 'ng'): void

  /**
   * オートコンプリート候補を取得
   * @param params - botId, tenantId, keyword, q, tags
   * @returns オートコンプリート候補
   */
  fetchComplete(params: {
    botId: string
    tenantId?: string | null
    keyword: string
    q: string[]
    tags: string[]
  }): Promise<{ complete: string[] }>

  /**
   * サジェストキーワードを取得
   * @param params - botId, tenantId, q, tags
   * @returns サジェストキーワード
   */
  fetchSuggest(params: {
    botId: string
    tenantId?: string | null
    q: string[]
    tags: string[]
  }): Promise<{ suggest: string[] }>

  /**
   * フィルター検索を実行
   * @param params - botId, q, page, tags, searchType, queryThreshold, tenantId
   * @returns 検索結果
   */
  filterSearch(params: {
    botId: string
    q: string[]
    page: number
    tags: string[]
    searchType?: string
    queryThreshold?: number
    tenantId?: string | null
  }): Promise<{
    search: {
      currentPage: number
      perPage: number
      total: number
      totalPages: number
      data: Array<{
        id: string
        title: string
        confidence?: number
      }>
    }
  }>

  /**
   * フィルター検索イベントを送信（アナリティクス用）
   * @param params - inputValue, count
   */
  sendFilterSearchEvent(params: { inputValue: string; count: number }): void

  /**
   * プルダウンクエリを実行してFAQ IDを取得
   * @param params - accountId, tenantId, query
   * @returns FAQ ID
   */
  fetchPulldownQuery(params: {
    accountId: string
    tenantId?: string | null
    query: string
  }): Promise<{ pulldownQuery: { id?: string } }>

  /**
   * スキルトークンを取得
   * @param params - accountId, skillId, session, param
   * @returns スキルトークン
   */
  fetchSkillToken(params: {
    accountId: string
    skillId: string
    session?: string
    param: any
  }): Promise<{ skillTokenV2: { id: string; token: string } }>
}

export const FaqDataContext = createContext<FaqDataClient | null>(null)

/**
 * FaqDataClientを取得するhook
 * @throws Contextが提供されていない場合はエラー
 */
export function useFaqData(): FaqDataClient {
  const client = useContext(FaqDataContext)
  if (!client) {
    throw new Error('FaqDataContext is not provided. Wrap your app with FaqDataContext.Provider')
  }
  return client
}
